/**
 * CreatureSize (Foundry: rule-element/creature-size.ts): change the Character's size (a size
 * name, or +1 / -1 steps), with the reach that follows and, when asked, the equipment's size.
 */
import { cleanRuleValue, resolveValue } from "../resolve";
import type { EngineState } from "../state";
import { SIZES, SIZE_SLUGS, SIZE_TO_REACH } from "../values";
import type { RuleHandler, RuleInstance } from "./base";

type Size = (typeof SIZES)[number];

interface CreatureSizeData {
  value: unknown;
  reach: { add?: unknown; upgrade?: unknown; override?: unknown } | null;
  resizeEquipment: boolean;
  minimumSize: Size | null;
  maximumSize: Size | null;
}

const WORD_TO_ABBREVIATION: Record<string, Size> = { tiny: "tiny", small: "sm", medium: "med", large: "lg", huge: "huge", gargantuan: "grg" };
const INCREMENT: Record<Size, Size> = { tiny: "sm", sm: "med", med: "lg", lg: "huge", huge: "grg", grg: "grg" };
const DECREMENT: Record<Size, Size> = { tiny: "tiny", sm: "tiny", med: "sm", lg: "med", huge: "lg", grg: "huge" };

function isSize(value: unknown): value is Size {
  return typeof value === "string" && (SIZES as readonly string[]).includes(value);
}

function sizeIndex(size: Size): number {
  return SIZES.indexOf(size);
}

function parse(rule: RuleInstance): void {
  const s = rule.source;
  const value = cleanRuleValue(s.value);
  if (value === undefined || !(typeof value === "number" || typeof value === "string")) return rule.fail("value: must be a size, +1 or -1");
  let reach: CreatureSizeData["reach"] = null;
  if (s.reach !== undefined && s.reach !== null) {
    if (typeof s.reach !== "object" || Array.isArray(s.reach)) return rule.fail("reach: must be an object");
    const record = s.reach as Record<string, unknown>;
    for (const key of Object.keys(record)) if (!["add", "upgrade", "override"].includes(key)) return rule.fail(`reach: "${key}" is not add, upgrade or override`);
    reach = { add: record.add === undefined ? undefined : cleanRuleValue(record.add), upgrade: record.upgrade === undefined ? undefined : cleanRuleValue(record.upgrade), override: record.override === undefined ? undefined : cleanRuleValue(record.override) };
  }
  for (const key of ["minimumSize", "maximumSize"] as const) if (s[key] !== undefined && !isSize(s[key])) return rule.fail(`${key}: "${String(s[key])}" is not a size`);
  rule.data = { value, reach, resizeEquipment: s.resizeEquipment === true, minimumSize: (s.minimumSize as Size | undefined) ?? null, maximumSize: (s.maximumSize as Size | undefined) ?? null } satisfies CreatureSizeData as unknown as Record<string, unknown>;
}

/** Apply a size change to the actor; shared with BattleForm. */
export function applyCreatureSize(rule: RuleInstance, state: EngineState, data: CreatureSizeData): void {
  const value = resolveValue(rule, state, data.value);
  if (rule.ignored) return;
  if (!(typeof value === "string" || typeof value === "number")) return rule.fail("has a non-string, non-numeric value");
  const size = typeof value === "string" ? (WORD_TO_ABBREVIATION[value] ?? value) : value;
  if (typeof size === "string" && !isSize(size)) return rule.fail(`"${size}" is not a recognized size`);
  const actor = state.actor;
  const originalSize = actor.system.traits.size.value as Size;
  if (value === 1) {
    if (data.maximumSize && sizeIndex(originalSize) >= sizeIndex(data.maximumSize)) return;
    actor.system.traits.size.value = INCREMENT[originalSize];
  } else if (value === -1) {
    if (data.minimumSize && sizeIndex(originalSize) <= sizeIndex(data.minimumSize)) return;
    actor.system.traits.size.value = DECREMENT[originalSize];
  } else if (isSize(size)) {
    actor.system.traits.size.value = size;
  } else {
    return rule.fail(`has an invalid value: must be one of "${[...Object.keys(WORD_TO_ABBREVIATION), ...SIZES].join('", "')}", +1, or -1`);
  }
  const reach = actor.system.attributes.reach;
  reach.base = getReach(rule, state, data, originalSize);
  reach.manipulate = Math.max(reach.manipulate, reach.base);
  if (data.resizeEquipment) {
    // The inventory (#11) resizes to the new size; until then the natural size is recorded so it can.
    actor.system.traits.naturalSize ??= originalSize;
  }
  // The size roll options were set before this phase: replace them with the new size's.
  const newSize = actor.system.traits.size.value;
  const index = sizeIndex(newSize as Size);
  for (let i = 0; i < SIZES.length; i++) {
    state.rollOptions.delete("all", `self:size:${i}`);
    state.rollOptions.delete("all", `self:size:${SIZE_SLUGS[i]}`);
  }
  state.rollOptions.set("all", `self:size:${index}`);
  state.rollOptions.set("all", `self:size:${SIZE_SLUGS[index]}`);
}

function getReach(rule: RuleInstance, state: EngineState, data: CreatureSizeData, originalSize: Size): number {
  const current = state.actor.system.attributes.reach.base;
  if (data.reach) {
    const source = data.reach.add ?? data.reach.upgrade ?? data.reach.override;
    const change = Math.trunc(Number(resolveValue(rule, state, source)));
    if (rule.ignored || !Number.isFinite(change)) return current;
    if (data.reach.add !== undefined && data.reach.add !== null) return Math.max(0, current + change);
    if (data.reach.upgrade !== undefined && data.reach.upgrade !== null) return Math.max(current, change);
    if (data.reach.override !== undefined && data.reach.override !== null) return Math.max(0, change);
  }
  const newSize = state.actor.system.traits.size.value as Size;
  return sizeIndex(newSize) > sizeIndex(originalSize) ? Math.max(SIZE_TO_REACH[newSize], current) : sizeIndex(newSize) < sizeIndex(originalSize) ? Math.min(SIZE_TO_REACH[newSize], current) : current;
}

function beforePrepareData(rule: RuleInstance, state: EngineState): void {
  if (!rule.test(state)) return;
  applyCreatureSize(rule, state, rule.data as unknown as CreatureSizeData);
}

export const CreatureSizeHandler: RuleHandler = { key: "CreatureSize", parse, beforePrepareData };
